<template>
  <section class="order-detail-section">
    <slot v-if="$slots.header && !isBnn" name="header" />

    <template v-if="isBnn && $slots.timer">
      <slot name="timer" />
    </template>

    <div class="order-detail-container">
      <div class="span-2">
        <div v-if="isBnn" class="flex items-center gap-2">
          <span>
            {{
              isVipStatusState()
                ? 'รายละเอียด'
                : 'รายละเอียดคำสั่งซื้อ'
            }}
          </span>
          <span class="font-bold"> #{{ data.order }} </span>
          <OrderDetailStatusBadge
            v-if="isBnn"
            :status="data.status"
          />
        </div>

        <div v-else class="headline-m">
          {{
            isVipStatusState() ? 'รายละเอียด' : 'รายละเอียดคำสั่งซื้อ'
          }}
        </div>
      </div>

      <template v-if="!isVipStatusState()">
        <div
          v-if="isBnn && $slots.flashMessagePayAtStore"
          class="span-2"
        >
          <slot name="flashMessagePayAtStore" />
        </div>

        <div v-if="isBnn" class="span-2">
          <div class="body-s text-gray-2">
            <div v-if="data.paid_at">
              วันที่ชำระเงิน:
              {{
                moment(data.paid_at).format('DD MMM YYYY HH:mm:ss')
              }}
            </div>
            <div>
              วิธีรับสินค้า:
              {{ isDelivery ? 'จัดส่ง' : 'รับที่สาขา' }}
            </div>
          </div>
        </div>

        <div
          v-if="
            $slots.appendOrderDetail &&
              data.status === ORDER_STATUS.COMPLETE
          "
          class="span-2"
        >
          <slot name="appendOrderDetail" />
        </div>

        <div
          v-if="isDeposit && data.status === ORDER_STATUS.COMPLETE"
          class="span-2"
        >
          <div class="body-s text-gray-2 mb-3">
            เลขที่ใบรับของ (บิลมัดจำ)
          </div>
          <div class="body-s mb-2">
            {{ depositNumber }}
          </div>
        </div>
      </template>

      <div>
        <div class="body-s text-gray-2 mb-3">
          ลูกค้า
        </div>
        <div class="body-s mb-2">
          {{ data.full_name }}
        </div>
        <div class="body-s mb-2">
          {{ data.email }}
        </div>
        <div class="body-s">
          {{ data.phone }}
        </div>
      </div>

      <template v-if="!isVipStatusState()">
        <div>
          <div class="body-s text-gray-2 mb-3">
            วิธีการชำระเงิน
          </div>
          <div class="title-medium mb-2">
            {{ finalPrice | currency2 }}
          </div>
          <div class="body-s mb-2">
            {{ data.payment_type }}
            <span v-if="data?.payment_selected?.term">
              ผ่อนชำระ {{ data.payment_selected.term }} เดือน ดอกเบี้ย
              {{ data.payment_selected.interest || 0 }}%
            </span>
          </div>
        </div>

        <template v-if="isDelivery">
          <div>
            <div class="body-s text-gray-2 mb-3">
              ที่อยู่จัดส่ง
            </div>
            <div class="body-s mb-2">
              {{ data.full_name }} ({{ data.phone }})
            </div>
            <div class="body-s">
              {{ fullBillAddress }}
            </div>
          </div>
        </template>

        <template v-else>
          <div>
            <div class="body-s text-gray-2 mb-3">
              รับที่สาขา
            </div>
            <div class="body-s mb-2">
              {{ receiveBranchName || 'ยังไม่ระบุสาขา' }}
            </div>
            <div
              v-if="data?.status !== ORDER_STATUS.CREATE"
              class="body-s"
            >
              {{ receiveBranchAddress }}
            </div>
          </div>
        </template>

        <div v-if="data.bill_address">
          <div class="body-s text-gray-2 mb-3">
            ที่อยู่ในใบเสร็จ
          </div>
          <div class="body-s mb-2">
            {{ data.bill_address.first_name }}
            {{ data.bill_address.last_name }} ({{
              data.bill_address.phone
            }})
          </div>
          <div class="body-s">
            {{ fullBillAddress }}
          </div>
        </div>

        <div v-if="data.tax_address" class="span-2">
          <div class="body-s text-gray-2 mb-3">
            ที่อยู่ในใบกำกับภาษี
            <template
              v-if="data.tax_address.tax_invoice_type == 'PERSONAL'"
            >
              (ภาษีบุคคล)
            </template>
            <template v-else>
              (นิติบุคคล)
            </template>
          </div>

          <div class="body-s mb-2">
            {{ data.tax_address.tax_id }}
          </div>
          <div class="body-s mb-2">
            <template
              v-if="data.tax_address.tax_invoice_type == 'PERSONAL'"
            >
              {{ data.tax_address.first_name }}
              {{ data.tax_address.last_name }}
            </template>
            <template v-else>
              {{ data.tax_address.company_name }}
              <span v-if="data.tax_address.company_code">
                ({{ data.tax_address.company_code }})
              </span>
            </template>
            ({{ data.tax_address.phone }})
          </div>
          <div class="body-s">
            {{ fullTaxAddress }}
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import moment from 'moment'
import isBnn from '@/config/isBnn'
import { ORDER_STATUS } from '@/config/order'

export default {
  name: 'OrderDetailSection',

  inject: ['isVipStatusState'],

  props: {
    data: {
      type: Object,
      required: true,
    },
    finalPrice: {
      type: [Number, String],
      default: 0,
    },
    isDeposit: {
      type: Boolean,
      default: false,
    },
    isDelivery: {
      type: Boolean,
      default: false,
    },
    depositNumber: {
      type: String,
      default: '',
    },
    receiveBranchName: {
      type: String,
      default: '',
    },
    receiveBranchAddress: {
      type: String,
      default: '',
    },
    fullBillAddress: {
      type: String,
      default: '',
    },
    fullTaxAddress: {
      type: String,
      default: '',
    },
  },

  components: {
    OrderDetailStatusBadge: () =>
      import('@/components/order/OrderDetailStatusBadge.vue'),
  },

  data() {
    return {
      ORDER_STATUS,
      isBnn,
      moment,
    }
  },
}
</script>

<style lang="stylus" scoped>
.order-detail-section {
	display: flex
	flex-direction: column
	gap: 24px
}

.-is-bnn {
	.order-detail-container {
		border: none
	}
}

.order-detail-container {
	background-color: white
    border: 1px solid $color-gray85
    border-radius: 8px
    padding: 16px

    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px

    @media screen and (min-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    .span-2 {
        grid-column: span 1

        @media screen and (min-width: 1024px) {
            grid-column: span 2
        }
    }
}
</style>
